import { NetworkStatus, useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
    PartialConfigDetailDocument,
    PartialConfigOverviewDocument,
    PartialConfigSummaryFragmentDoc,
} from '@/graphql/types';
import type {
    PartialConfigOverviewQuery,
    PartialConfigOverviewQueryVariables,
    PartialConfigSummaryFragment,
} from '@/graphql/types';
import { DEFAULT_PAGE_SIZE } from '../../table/hooks';

export const PARTIAL_CONFIG_SUMMARY_FRAGMENT = PartialConfigSummaryFragmentDoc;

export const PARTIAL_CONFIG_DETAIL_QUERY = PartialConfigDetailDocument;

export const PARTIAL_CONFIGS_QUERY = PartialConfigOverviewDocument;

type OverviewConnection = PartialConfigOverviewQuery['partialConfigs'];

function isOverviewNode(value: unknown): value is PartialConfigSummaryFragment {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const candidate = value as Partial<Record<keyof PartialConfigSummaryFragment, unknown>>;

    return (
        typeof candidate.id === 'string' &&
        typeof candidate.name === 'string' &&
        typeof candidate.swarmType === 'string' &&
        typeof candidate.partialConfigType === 'string' &&
        candidate.modifiedAt !== undefined &&
        candidate.createdAt !== undefined
    );
}

type UsePartialConfigOverviewOptions = {
    pageSize?: number;
    filters?: Partial<Omit<PartialConfigOverviewQueryVariables, 'first' | 'after'>>;
};

type UsePartialConfigOverviewResult = {
    connection: OverviewConnection | null | undefined;
    nodes: PartialConfigSummaryFragment[];
    totalCount: number;
    hasNextPage: boolean;
    isInitialLoading: boolean;
    isFetchingMore: boolean;
    loadMore: () => Promise<void> | void;
    error: ApolloError | undefined;
};

export function usePartialConfigOverview(
    options: UsePartialConfigOverviewOptions = {}
): UsePartialConfigOverviewResult {
    const { pageSize = DEFAULT_PAGE_SIZE, filters } = options;

    const variables = useMemo<PartialConfigOverviewQueryVariables>(
        () => ({
            first: pageSize,
            ...pick(filters, ['swarmType', 'partialConfigType', 'search']),
        }),
        [pageSize, filters]
    );

    const { data, loading, fetchMore, networkStatus, error } = useQuery<
        PartialConfigOverviewQuery,
        PartialConfigOverviewQueryVariables
    >(PARTIAL_CONFIGS_QUERY, {
        variables,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const connection = data?.partialConfigs;
    const edges = connection?.edges;

    const nodes = useMemo(
        () => (edges ?? []).map((edge) => edge?.node).filter(isOverviewNode),
        [edges]
    );

    const hasNextPage = Boolean(connection?.pageInfo?.hasNextPage);
    const endCursor = connection?.pageInfo?.endCursor ?? null;

    const loadMore = useCallback(() => {
        if (!hasNextPage || !endCursor) {
            return;
        }

        void fetchMore<PartialConfigOverviewQuery, PartialConfigOverviewQueryVariables>({
            variables: {
                ...variables,
                after: endCursor,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const previousConnection = previousResult?.partialConfigs;
                const previousEdges = previousConnection?.edges ?? [];
                const nextConnection = fetchMoreResult?.partialConfigs;

                if (!nextConnection) {
                    return previousResult;
                }

                const newEdges = [...previousEdges];

                nextConnection.edges?.forEach((edge) => {
                    if (!edge) {
                        return;
                    }

                    const hasEdge = newEdges.some((existing) => existing?.cursor === edge.cursor);

                    if (!hasEdge) {
                        newEdges.push(edge);
                    }
                });

                return {
                    __typename: previousResult.__typename,
                    partialConfigs: {
                        ...nextConnection,
                        edges: newEdges,
                    },
                };
            },
        });
    }, [endCursor, fetchMore, hasNextPage, variables]);

    return {
        connection,
        nodes,
        totalCount: connection?.totalCount ?? 0,
        hasNextPage,
        isInitialLoading: loading && networkStatus === NetworkStatus.loading,
        isFetchingMore: loading && networkStatus === NetworkStatus.fetchMore,
        loadMore,
        error,
    };
}
